import { Check, Sparkles, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Reveal, Overline } from "@/components/site/Reveal";
import { scrollToId } from "@/lib/site";

const PLANS = {
  monthly: [
    { id: "specific_monthly", name: "Specific Subjects", featured: false, tag: null,
      features: ["Pick the subjects you need", "3–4 classes per subject / week", "Alternate-week tests & doubt sessions", "1-on-1 or small batch"] },
    { id: "all_monthly", name: "All Subjects", featured: true, tag: "Most popular",
      features: ["Every subject, one plan", "5 days a week", "1.5 to 2.5 hrs per day by grade", "Full syllabus coverage + assessments"] },
  ],
  halfyear: [
    { id: "specific_halfyear", name: "Specific Subjects", featured: false, tag: "Save 15 days",
      features: ["Everything in monthly", "15-day discount built in", "Priority scheduling", "Termly progress report"] },
    { id: "all_halfyear", name: "All Subjects", featured: true, tag: "Save 15 days",
      features: ["Everything in monthly", "15-day discount built in", "Priority scheduling", "Termly progress report"] },
  ],
  yearly: [
    { id: "specific_yearly", name: "Specific Subjects", featured: false, tag: "Save 1 month",
      features: ["Everything in monthly", "1-month discount built in", "Full-session continuity", "Dedicated mentor"] },
    { id: "all_yearly", name: "All Subjects", featured: true, tag: "Save 1 month",
      features: ["Everything in monthly", "1-month discount built in", "Full-session continuity", "Dedicated mentor"] },
  ],
};

export const Plans = () => {
  return (
    <section id="plans" className="py-24 md:py-32 bg-muted">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <Overline>Core plans</Overline>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight max-w-2xl">A full learning solution matched to your child's grade & board.</h2>
          <p className="mt-4 text-muted-foreground max-w-xl">Choose monthly for flexibility, or save with half-yearly and yearly plans.</p>
        </Reveal>

        <Reveal delay={0.1}>
          <Tabs defaultValue="monthly" className="mt-10">
            <TabsList data-testid="plans-tabs" className="bg-white rounded-full p-1.5 h-auto border border-black/5 shadow-sm">
              <TabsTrigger value="monthly" data-testid="tab-monthly" className="rounded-full px-6 py-2.5 font-label data-[state=active]:bg-primary data-[state=active]:text-white">Monthly</TabsTrigger>
              <TabsTrigger value="halfyear" data-testid="tab-halfyear" className="rounded-full px-6 py-2.5 font-label data-[state=active]:bg-primary data-[state=active]:text-white">Half-Yearly</TabsTrigger>
              <TabsTrigger value="yearly" data-testid="tab-yearly" className="rounded-full px-6 py-2.5 font-label data-[state=active]:bg-primary data-[state=active]:text-white">Yearly</TabsTrigger>
            </TabsList>

            {Object.entries(PLANS).map(([key, plans]) => (
              <TabsContent key={key} value={key} className="mt-10">
                <div className="grid md:grid-cols-2 gap-6">
                  {plans.map((p) => {
                    const featured = p.featured;
                    return (
                      <div key={p.id} data-testid={`plan-card-${p.id}`}
                        className={`rounded-3xl p-8 md:p-10 border relative overflow-hidden transition-transform duration-300 hover:-translate-y-2 ${featured ? "bg-primary text-primary-foreground border-primary shadow-2xl" : "bg-white border-4 border-secondary shadow-[0_8px_30px_rgb(0,0,0,0.04)]"}`}>
                        {p.tag && (
                          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold mb-5 ${featured ? "bg-accent text-accent-foreground" : "bg-secondary/15 text-secondary"}`}>
                            {featured && <Sparkles size={13} />}{p.tag}
                          </span>
                        )}
                        <h3 className="font-heading font-bold text-2xl">{p.name}</h3>
                        <ul className="mt-7 space-y-3">
                          {p.features.map((f) => (
                            <li key={f} className="flex items-start gap-3 text-sm">
                              <Check size={18} className={featured ? "text-accent shrink-0 mt-0.5" : "text-secondary shrink-0 mt-0.5"} />
                              <span className={featured ? "text-white/90" : "text-foreground/80"}>{f}</span>
                            </li>
                          ))}
                        </ul>
                        <button
                          data-testid={`enroll-btn-${p.id}`}
                          onClick={() => scrollToId("#demo")}
                          className={`mt-8 w-full rounded-full py-4 font-bold flex items-center justify-center gap-2 transition-[transform,box-shadow,background-color] duration-300 ${featured ? "bg-accent text-accent-foreground hover:-translate-y-0.5" : "bg-primary text-white hover:-translate-y-0.5 hover:shadow-lg"}`}
                        >
                          Book a Free Demo <ArrowRight size={18} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </Reveal>
        <p className="mt-6 text-xs text-muted-foreground text-center">Book a free demo and our academic counsellor will share pricing and a plan tailored to your child's grade, board and goals.</p>
      </div>
    </section>
  );
};

export default Plans;
